import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { getData, postData } from '../middleware/apiMiddleware';
import s from './ComplexSentence.module.css';
import { useNavigate } from "react-router-dom";
import { GreenButton, BlueButton, RedButton } from './MyButton';
import MyComponentLoading from '../components/Utilities/MyComponentLoading';
import {MyInput} from './Utilities/MyInput';


const MyTextarea = ({ title, setter, getter }) => {
    return (
        <div className={s.formGroup}>
            <label className={s.formLabel}>{title}</label>
            <textarea
                value={getter}
                className={s.textarea}
                rows="5"
                wrap='off'
                onChange={(e) => setter(e.target.value)}
            />
        </div>
    );
};

// --- Custom Components to replace react-bootstrap ---
const AudioPlayer = ({ script, audioUrl, highlights, title }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [activeHighlightId, setActiveHighlightId] = useState(null);

    // Toggle play/pause
    const togglePlay = () => {
        const audio = audioRef.current;
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    // Update state when audio time changes
    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        setCurrentTime(audio.currentTime);

        // Find which word to highlight
        const currentHighlight = highlights.find(h => 
            audio.currentTime >= h.start && audio.currentTime <= h.end
        );
        
        setActiveHighlightId(currentHighlight ? currentHighlight.id : null);
    };

    // Reset when audio finishes
    const handleAudioEnded = () => {
        setIsPlaying(false);
        setActiveHighlightId(null);
        // Optional: Reset time to 0 if you want the play button to restart
        // audioRef.current.currentTime = 0; 
    };

    // Helper to render the script with highlights
    const renderScriptWithHighlights = () => {
        let lastIndex = 0;
        const parts = [];
        
        // Find all occurrences of the highlighted texts and replace them
        highlights.forEach(h => {
            const regex = new RegExp(h.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
            let match;
            while ((match = regex.exec(script)) !== null) {
                // If this is the currently active highlight, wrap it in a styled span
                if (h.id === activeHighlightId) {
                    // Add text before the match
                    parts.push(script.substring(lastIndex, match.index));
                    // Add the highlighted text
                    parts.push(
                        <span key={`${h.id}-${match.index}`} className={s.audioHighlight}>
                            {match[0]}
                        </span>
                    );
                    lastIndex = regex.lastIndex;
                }
            }
        });
        
        // Add any remaining text after the last highlight
        parts.push(script.substring(lastIndex));
        
        // This is a simplified approach. A more robust solution might need to
        // parse the HTML string to avoid breaking tags. For now, we'll use
        // dangerouslySetInnerHTML and inject the highlight class.
        
        let highlightedScript = script;
        const activeHighlight = highlights.find(h => h.id === activeHighlightId);
        
        if(activeHighlight) {
            // This is a safer way to highlight without complex regex on HTML
            const highlightTag = `<span class="${s.audioHighlight}">${activeHighlight.text}</span>`;
            highlightedScript = script.replace(new RegExp(`\\b${activeHighlight.text}\\b`, 'g'), highlightTag);
        }

        return <p dangerouslySetInnerHTML={{ __html: highlightedScript }} />;
    };

    return (
        <div className={s.audioPlayerContainer}>
            <h6>{title}</h6>
            <button onClick={togglePlay} className={s.playButton}>
                {isPlaying ? '❚❚' : '▶'}
            </button>
            <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleAudioEnded}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            />
            <div className={s.scriptContainer}>
                {renderScriptWithHighlights()}
            </div>
        </div>
    );
};
// A simple Tabs component
const CustomTabs = ({ children }) => {
    const [activeTab, setActiveTab] = useState(children[0].props.label);

    const handleClick = (e, newActiveTab) => {
        e.preventDefault();
        setActiveTab(newActiveTab);
    };

    return (
        <div className={s.tabContainer}>
            <ul className={s.tabNav}>
                {children.map((child) => (
                    <li
                        key={child.props.label}
                        className={activeTab === child.props.label ? s.tabActive : ""}
                    >
                        <a href="#" onClick={(e) => handleClick(e, child.props.label)}>
                            {child.props.label}
                        </a>
                    </li>
                ))}
            </ul>
            <div className={s.tabContent}>
                {children.map((child) => {
                    if (child.props.label === activeTab) {
                        return <div key={child.props.label}>{child.props.children}</div>;
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

// A simple Collapsible/Accordion component
const CustomCollapsible = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className={s.collapsibleItem}>
            <button className={s.collapsibleHeader} onClick={() => setIsOpen(!isOpen)}>
                {title}
                <span className={s.collapsibleIcon}>{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && <div className={s.collapsibleBody}>{children}</div>}
        </div>
    );
};


// --- Display Component for Vocabulary JSON ---
const VocabularyJsonDisplay = ({ jsonData }) => {
  if (!jsonData) return <div className={s.card}>No vocabulary data to display.</div>;

  // Destructure all the parts we need from the JSON
  const { translation, analysis, tutoring } = jsonData;

  // Check if audio data exists to conditionally render the players
  const hasCantoneseAudio = tutoring?.cantoneseAudioUrl && tutoring?.cantoneseAudioHighlights;
  const hasMandarinAudio = tutoring?.mandarinAudioUrl && tutoring?.mandarinAudioHighlights;

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>Vocabulary Analysis</div>
      <div className={s.cardBody}>
        
        {/* --- 1. Translation Section (RESTORED) --- */}
        {translation && (
            <CustomCollapsible title="Translation" defaultOpen={true}>
                <ul className={s.listGroup}>
                    <li className={s.listItem}><strong>Traditional Chinese:</strong> {translation.traditional_chinese}</li>
                    <li className={s.listItem}><strong>Simplified Chinese:</strong> {translation.simplified_chinese}</li>
                </ul>
            </CustomCollapsible>
        )}

        {/* --- 2. Keywords & Expressions Section (RESTORED) --- */}
        {analysis && (
            <CustomCollapsible title="Keywords & Expressions" defaultOpen={true}>
                {analysis.key_words && (
                    <>
                        <h6>Key Words</h6>
                        {analysis.key_words.map((word, index) => (
                            <div key={index} className={`${s.card} ${s.innerCard}`}>
                                <div className={s.cardBody}>
                                    <h5 className={s.cardTitle}>{word.keywords} <small>({word.type})</small></h5>
                                    <p><strong>Traditional:</strong> {word.explanation.traditional_chinese}</p>
                                    <p><strong>Simplified:</strong> {word.explanation.simplified_chinese}</p>
                                </div>
                            </div>
                        ))}
                    </>
                )}
                
                {analysis.expressions && (
                    <>
                        <h6 style={{marginTop: '1rem'}}>Expressions</h6>
                        {analysis.expressions.map((expr, index) => (
                            <div key={index} className={`${s.card} ${s.innerCard}`}>
                                <div className={s.cardBody}>
                                    <h5 className={s.cardTitle}>{expr.term} <small>({expr.type})</small></h5>
                                    <p><strong>Traditional:</strong> {expr.explanation.traditional_chinese}</p>
                                    <p><strong>Simplified:</strong> {expr.explanation.simplified_chinese}</p>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </CustomCollapsible>
        )}
        
        {/* --- 3. Tutoring Scripts Section (WITH AUDIO PLAYER) --- */}
        {tutoring && (
            <CustomCollapsible title="Tutoring Scripts" defaultOpen={true}>
                {hasCantoneseAudio ? (
                    <AudioPlayer 
                        title="Cantonese Script"
                        script={tutoring.cantonese_script}
                        audioUrl={tutoring.cantoneseAudioUrl}
                        highlights={tutoring.cantoneseAudioHighlights}
                    />
                ) : (
                    tutoring.cantonese_script && (
                        <>
                            <h6>Cantonese Script</h6>
                            <p dangerouslySetInnerHTML={{ __html: tutoring.cantonese_script }} />
                        </>
                    )
                )}

                <hr />

                {hasMandarinAudio ? (
                    <AudioPlayer 
                        title="Mandarin Script"
                        script={tutoring.mandarin_script}
                        audioUrl={tutoring.mandarinAudioUrl}
                        highlights={tutoring.mandarinAudioHighlights}
                    />
                ) : (
                    tutoring.mandarin_script && (
                        <>
                            <h6>Mandarin Script</h6>
                            <p dangerouslySetInnerHTML={{ __html: tutoring.mandarin_script }} />
                        </>
                    )
                )}
            </CustomCollapsible>
        )}
      </div>
    </div>
  );
};

// --- Display Component for Parse Tree JSON ---
const TreeNode = ({ node, level = 0 }) => (
    <div className={s.treeNode} style={{ paddingLeft: `${level * 20}px` }}>
        <div className={s.treeContent}>
            <strong className={s.treeLabel}>{node.label}</strong> <span className={s.treeType}>({node.type})</span>
            {node.word && <div className={s.treeWord}>Word: "{node.word}"</div>}
            {node.explanation && <div className={s.treeExplanation}>{node.explanation}</div>}
        </div>
        {node.children && (
            <div className={s.treeChildren}>
                {node.children.map((child, index) => (
                    <TreeNode key={index} node={child} level={level + 1} />
                ))}
            </div>
        )}
    </div>
);
const getSentenceSpan = (node) => {
    // Note: The API data uses o-prefixed keys (word, children)
    if (node.word) {
        return node.word;
    }
    if (node.children && node.children.length > 0) {
        let text = node.children.map(getSentenceSpan).join(' ');
        // Clean up spacing around punctuation for a cleaner look
        return text
            .replace(/ (\’re|\’s|\’t|\’ve|\’ll|\’d|’m)/g, '$1')
            .replace(/ (\.|,|\?|!|:|;)/g, '$1');
    }
    return '';
};

// The recursive TreeNode component
const InteractiveTreeNode = ({ node, depth = 0 }) => {
    // Nodes at depth 0 and 1 are expanded by default, as in the prottype
    const [isExpanded, setIsExpanded] = useState(depth < 2);
    
    // Check if the node is a leaf (has no children)
    const isLeaf = !node.children || node.children.length === 0;

    const sentenceSpan = useMemo(() => getSentenceSpan(node), [node]);

    const toggleExpansion = () => {
        if (!isLeaf) {
            setIsExpanded(prev => !prev);
        }
    };

    return (
        <div className={`${s.treeNode} ${s['depth-' + depth]} ${isExpanded ? s.initialExpanded : ''} ${isLeaf ? s.leafNode : ''}`}>
            <div className={s.nodeHeader} onClick={toggleExpansion}>
                <span className={s.toggleIndicator}>
                    {isLeaf ? '' : (isExpanded ? '▼' : '▶')}
                </span>
                <span className={s.nodeInfo}>
                    <span className={s.nodeLabel}>{node.label}</span>
                </span>
                <span className={s.nodeSentenceSpan}>
                    {sentenceSpan}
                </span>
            </div>

            {node.explanation && (
                <p className={s.nodeExplanation}>{node.explanation}</p>
            )}

            {!isLeaf && (
                <div className={`${s.nodeChildren} ${isExpanded ? s.expanded : ''}`}>
                    {node.children.map((child, index) => (
                        <InteractiveTreeNode key={index} node={child} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ParseTreeJsonDisplay = ({ jsonData }) => {
    // Make sure to call hooks at the top level
    if (!jsonData || !jsonData.tree) {
        return <div className={s.card}>No parse tree data to display.</div>;
    }

    return (
        <div className={s.parseTreeContainer}>
            <h2>Sentence Parse Tree</h2>
            <p className={s.highlightSentence}>{jsonData.sentence}</p>
            <InteractiveTreeNode node={jsonData.tree} />
        </div>
    );
};
const SentenceStructureJsonDisplay = ({ jsonData }) => {
  // 1. ALL HOOKS ARE CALLED AT THE TOP LEVEL, UNCONDITIONALLY.
  const [activeComponentId, setActiveComponentId] = useState(null);
  const [arrowPath, setArrowPath] = useState('');
  const componentRefs = useRef({}); // To store refs to DOM elements { id: element }
  const wrapperRef = useRef(null); // Ref for the main container

  // A color map for different component types, inspired by the prottype
  const colorMap = {
    'Subject': { bg: '#fee2e2', border: '#fca5a5', text: '#b91c1c' },
    'Verb': { bg: '#e0e7ff', border: '#a5b4fc', text: '#4f46e5' },
    'Direct Object': { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
    'default': { bg: '#dcfce7', border: '#86efac', text: '#16a34a' }
  };
  
  // The useMemo hook is also called unconditionally. Its logic only runs when jsonData changes.
  const allComponents = useMemo(() => {
    // If jsonData doesn't exist yet, return an empty array to prevent errors.
    if (!jsonData) return []; 

    let idCounter = 0;
    const components = [];

    if (jsonData.eliminatedOrder) {
      jsonData.eliminatedOrder.forEach(item => {
        components.push({
          id: `comp-${idCounter++}`,
          isBubble: false,
          isMainComponent: item.isFromMainClause,
          componentType: item.componentName,
          text: item.text,
          explanation: item.explanation,
          modifies: item.modifies,
          order: item.order,
        });
      });
    }
    
    if (jsonData.mainClauseBreakdown && jsonData.mainClauseBreakdown.highlightAndBubble) {
      jsonData.mainClauseBreakdown.highlightAndBubble.forEach(clause => {
        clause.analysis.forEach(item => {
          components.push({
            id: `comp-${idCounter++}`,
            isBubble: true,
            isMainComponent: item.isMainComponent,
            componentType: item.componentType,
            text: item.bubbleText,
            highlightText: item.highlightText,
            explanation: item.explanation,
            conjunction: clause.conjunction,
          });
        })
      })
    }
    return components;
  }, [jsonData]);

  // Effect to draw/clear the SVG arrow
  useEffect(() => {
    const activeComponent = allComponents.find(c => c.id === activeComponentId);
    
    if (!activeComponent || !activeComponent.modifies) {
      setArrowPath('');
      return;
    }

    const startElement = componentRefs.current[activeComponent.id];
    const targetComponent = allComponents.find(c => c.text === activeComponent.modifies);
    const endElement = targetComponent ? componentRefs.current[targetComponent.id] : null;
    const wrapper = wrapperRef.current;

    if (startElement && endElement && wrapper) {
      const svgRect = wrapper.getBoundingClientRect();
      const startRect = startElement.getBoundingClientRect();
      const endRect = endElement.getBoundingClientRect();
      
      const startX = startRect.left + startRect.width / 2 - svgRect.left;
      const startY = startRect.top - svgRect.top;
      
      const endX = endRect.left + endRect.width / 2 - svgRect.left;
      const endY = endRect.bottom - svgRect.top;

      const controlX = (startX + endX) / 2;
      const controlY = Math.min(startY, endY) - (Math.abs(startX - endX) * 0.2 + 40);

      setArrowPath(`M ${startX} ${startY} Q ${controlX} ${controlY}, ${endX} ${endY}`);
    }
  }, [activeComponentId, allComponents]);

  // 2. THE CONDITIONAL RETURN IS NOW *AFTER* THE HOOKS. THIS IS ALLOWED.
  if (!jsonData) {
      return <div className={s.card}>No sentence structure data to display.</div>;
  }

  // --- The rest of the component logic remains the same ---

  const bubbleComponents = allComponents.filter(c => c.isBubble);
  const eliminatedComponents = allComponents.filter(c => !c.isBubble).sort((a,b) => a.order - b.order);
  
  const handleInteraction = (component) => {
    if (activeComponentId === component.id) {
      setActiveComponentId(null);
    } else {
      setActiveComponentId(component.id);
    }
  };

  const renderHighlightedText = (text, highlight) => {
    if (!highlight || !text.includes(highlight)) {
      return text;
    }
    const parts = text.split(new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span key={i} className={s.highlightedText}>{part}</span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // The JSX returned here is unchanged
  return (
    <div className={s.appContainer}>
      <h2 className={s.structureH2}>Sentence Structure Breakdown</h2>
        <div className={s.sentenceWrapper} ref={wrapperRef}>
            <svg className={s.arrowCanvas}>
                <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" />
                    </marker>
                </defs>
                {arrowPath && <path d={arrowPath} className={s.arrow} markerEnd="url(#arrowhead)" />}
            </svg>
            
            <div className={s.sentence}>
                {eliminatedComponents.map(item => (
                    <div
                        key={item.id}
                        ref={el => (componentRefs.current[item.id] = el)}
                        className={`${s.componentGroup} ${item.id === activeComponentId ? s.strikethrough : ''}`}
                        onClick={() => handleInteraction(item)}
                    >
                        <div className={`${s.eliminatedText} ${item.id === activeComponentId ? s.highlight : ''}`}>
                            {item.text}
                        </div>
                        <span className={s.componentTypeLabel}>{item.componentType}</span>
                    </div>
                ))}
                
                {bubbleComponents.map(item => (
                    <div
                        key={item.id}
                        ref={el => (componentRefs.current[item.id] = el)}
                        className={s.componentGroup}
                        onClick={() => handleInteraction(item)}
                    >
                        <div 
                            className={`${s.bubbleContent} ${!item.isMainComponent ? s.nonMainComponent : ''} ${item.id === activeComponentId ? s.highlight : ''}`}
                            style={item.isMainComponent ? {
                                backgroundColor: (colorMap[item.componentType] || colorMap.default).bg,
                                borderColor: (colorMap[item.componentType] || colorMap.default).border,
                            } : {}}
                        >
                            {renderHighlightedText(item.text, item.highlightText)}
                        </div>
                        <span 
                            className={s.componentTypeLabel} 
                            style={item.isMainComponent ? { color: (colorMap[item.componentType] || colorMap.default).text } : {}}
                        >
                            {item.componentType}
                        </span>
                    </div>
                ))}
            </div>
      </div>

      <h2 className={s.structureH2}>Explanations</h2>
      <ul className={s.explanationList}>
        {allComponents.map(item => (
          <li
            key={item.id}
            onClick={() => handleInteraction(item)}
            className={item.id === activeComponentId ? s.highlight : ''}
            style={item.isBubble && item.isMainComponent ? {
                borderColor: (colorMap[item.componentType] || colorMap.default).border
            }: {}}
          >
            <div className={s.explanationHeader}>{item.text}</div>
            <strong>{item.componentType}:</strong>
            <div className={s.explanationBody} dangerouslySetInnerHTML={{ __html: item.explanation }} />
          </li>
        ))}
      </ul>
    </div>
  );
};



export default function ComplexSentence({ itemId }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const res = await getData('complexSentenceApi/getDetail?itemId=' + itemId, navigate);
            if (res.data) {
                setData(res.data);
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
    }, [itemId, navigate]);
		
		 useEffect(() => {
			fetchData();
		}, [fetchData]);


		useEffect(() => {
			// Only start polling if the component has data and is_ai_generating is 1.

			if (data && data.is_ai_generating === 1) {
				console.log("AI is generating, starting to poll for status...");
				
				// Set up an interval to call fetchData every 5 seconds
				const intervalId = setInterval(() => {
					console.log("Polling for AI completion...");
					fetchData();
				}, 20000); // Poll every 5 seconds

				// This is a crucial cleanup function. It runs when the component unmounts
				// or when the dependencies (data) change. If is_ai_generating becomes 0,
				// this cleanup will run and stop the interval.
				return () => {
					console.log("Stopping polling.");
					clearInterval(intervalId);
				};
			}
			// If not generating, do nothing. The effect will not set up an interval.
		}, [data, fetchData]);

    const updateData = async () => {
        let obj = { itemId, 'data': data };
        await postData("complexSentenceApi/updateComplexSentence", obj, navigate);
        //alert('Update successful!');
    };

    const genAi = async () => {
				if (!data || !data.main_sentence || !data.title) {
						alert('Title and Main Sentence cannot be empty.');
						return;
				}
				let main_sentence = data.main_sentence;
				let title = data.title;
				let obj = { itemId, title, main_sentence, force:true };

				// --- THIS IS THE FIX ---
				// Instead of setting data to null, we spread the previous data
				// and just overwrite the 'is_ai_generating' flag.
				setData(prevData => ({
						...prevData,
						is_ai_generating: 1,
				}));

				// Now, we call the backend. The UI is already showing the "generating" message.
				await postData("complexSentenceApi/genAi", obj, navigate);
				
				// We don't even need to call fetchData() here. The polling useEffect
				// will see that is_ai_generating is now 1 and will start polling automatically.
		};

    const parsedData = useMemo(() => {
        if (!data) return { vocab: null, tree: null, structure: null };
        try {
            return {
                vocab: data.vocabulary_json ? JSON.parse(data.vocabulary_json) : null,
                tree: data.parse_tree_json ? JSON.parse(data.parse_tree_json) : null,
                structure: data.sentence_structure_json ? JSON.parse(data.sentence_structure_json) : null
            };
        } catch (e) {
            console.error("Failed to parse JSON data:", e);
            return { vocab: null, tree: null, structure: null };
        }
    }, [data]);

		const cancelGeneration = () => {
			console.log("User cancelled AI generation locally.");
			// This is a client-side only change. It updates the state to show the
			// editor immediately, without waiting for the backend.
			setData(prevData => ({
					...prevData,
					is_ai_generating: 0,
			}));
		};

    if (!data) {
        return <MyComponentLoading />;
    }
		
		if (data.is_ai_generating === 1) {
			return (
					<div className={s.aiGeneratingContainer}>
							<h3>The AI is generating...</h3>
							<p>This page will automatically refresh when complete. Do you want to cancel?</p>
							<div style={{marginTop: '1rem'}}>
									<RedButton clickedAction={cancelGeneration} bText={"Cancel Generation"} />
							</div>
					</div>
			);
		}
		
    return (
        <div style={{ textAlign: "left", margin: "20px" }}>
            <div className={s.headerContainer}>
                 <h2 className={s.headerTitle}>Complex Sentence Editor</h2>
                 <a href="http://json.parser.online.fr/" target="_blank" rel="noopener noreferrer">JSON Formatter Tool</a>
            </div>

            <div className={s.editorBox}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <div style={{ fontWeight: "bold", minWidth: "50px" }}>Title:</div>
                    <MyInput inputLabel={""} dataInput={data.title} changeAction={
                        (v) => setData(prevData => ({ ...prevData, title: v }))
                    } />
                </div>

                <MyTextarea title={"Main Sentence"} setter={(v) => setData(prevData => ({ ...prevData, main_sentence: v }))} getter={data.main_sentence} />
                
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
									<div style={{ fontWeight: "bold", minWidth: "150px" }}>Grammar Trait:</div>
									<MyInput inputLabel={""} dataInput={data.grammar_trait} changeAction={
											(v) => setData(prevData => ({ ...prevData, grammar_trait: v }))
									} />
                </div>
								
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
									<div style={{ fontWeight: "bold", minWidth: "150px" }}>Key Caluse Type:</div>
									<MyInput inputLabel={""} dataInput={data.key_clause_type} changeAction={
											(v) => setData(prevData => ({ ...prevData, key_clause_type: v }))
									} />
                </div>
								
								<BlueButton clickedAction={updateData} bText={"Update"} />
								
                <div style={{ display: "flex", gap: "10px", marginTop:"10px" }}>
                    <GreenButton clickedAction={genAi} bText={"Update and AI Generate detail"} />
										
                </div>
            </div>

            <div style={{marginTop: '2rem'}}>
                <CustomTabs>
                    <div label="Vocabulary">
                        <VocabularyJsonDisplay jsonData={parsedData.vocab} />
                    </div>
                    <div label="Parse Tree">
                        <ParseTreeJsonDisplay jsonData={parsedData.tree} />
                    </div>
                    <div label="Sentence Structure">
                        <SentenceStructureJsonDisplay jsonData={parsedData.structure} />
                    </div>
                </CustomTabs>
            </div>
        </div>
    );
}